/* global BigInt */
// src/utils/multicall.js
import { ethers } from 'ethers';
import positionManagerABI from './abis/PositionManager.json';
import { NETWORKS } from './constants';

const dataPoolGetterABI = [
    'function getDepositedAndUnclaimedFees(uint256) view returns (uint256, uint256, uint256, uint256)',
    'function getExtraPositionInfo(uint256) view returns (tuple(string, uint8, string, uint8, uint160, int24, bool, uint256))',
];

// Cache for providers and contracts per chain
const cache = {
    providers: {},
    contracts: {}
};

function getNetworkResources(chain) {
    if (!cache.providers[chain]) {
        const config = NETWORKS[chain];
        if (!config) throw new Error(`Network ${chain} not configured`);

        const provider = new ethers.JsonRpcProvider(config.rpc);
        const positionManager = new ethers.Contract(config.positionManager, positionManagerABI, provider);
        const dataPoolGetter = new ethers.Contract(config.dataPoolGetter, dataPoolGetterABI, provider);

        cache.providers[chain] = provider;
        cache.contracts[chain] = { positionManager, dataPoolGetter };
    }
    return cache.contracts[chain];
}

// Token symbol to CoinGecko ID mapping
const tokenToId = {
    'eth': 'ethereum',
    'wbnb': 'binancecoin',
    'usdc': 'usd-coin',
    'usdt': 'tether',
    'dai': 'dai',
    'wbtc': 'bitcoin',
    'virtual': 'virtual-protocol',
    'weth': 'ethereum',
};

// Cache for prices
let priceCache = {};
let lastFetchTime = 0;
const CACHE_DURATION = 60000; // 60 seconds

async function fetchPrices(ids) {
    const now = Date.now();
    if (now - lastFetchTime < CACHE_DURATION && Object.keys(priceCache).length > 0) {
        return priceCache;
    }

    try {
        const response = await fetch(
            `https://api.coingecko.com/api/v3/simple/price?ids=${ids.join(',')}&vs_currencies=usd`
        );
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const prices = await response.json();
        priceCache = prices;
        lastFetchTime = now;
        return prices;
    } catch (error) {
        console.error('Error fetching prices from CoinGecko:', error);
        return {};
    }
}

export const fetchPositionsData = async (positionsList) => {
    if (positionsList.length === 0) {
        return [];
    }

    const positionsData = await Promise.all(
        positionsList.map(async ({ id: tokenId, chain }) => {
            try {
                const { positionManager, dataPoolGetter } = getNetworkResources(chain);

                // Direct calls
                const posResult = await positionManager.positions(BigInt(tokenId));
                const extraResult = await dataPoolGetter.getExtraPositionInfo(BigInt(tokenId));
                const depResult = await dataPoolGetter.getDepositedAndUnclaimedFees(BigInt(tokenId));


                // Extract needed fields from posResult
                const token0 = posResult[2].toLowerCase();
                const token1 = posResult[3].toLowerCase();
                const fee = posResult[4];
                const tickLower = posResult[5];
                const tickUpper = posResult[6];
                const liquidity = posResult[7];

                // extraResult is array since unnamed tuple
                const [
                    token0Symbol,
                    token0Decimals,
                    token1Symbol,
                    token1Decimals,
                    sqrtPriceX96,
                    currentTick,
                    isInMasterChef,
                    pendingCakeAmount,
                ] = extraResult;

                // depResult
                const [amount0, amount1, fees0, fees1] = depResult;

                const dec0 = Number(token0Decimals);
                const dec1 = Number(token1Decimals);

                // Compute prices
                const priceLower = Math.pow(1.0001, Number(tickLower)) * Math.pow(10, dec0 - dec1);
                const priceUpper = Math.pow(1.0001, Number(tickUpper)) * Math.pow(10, dec0 - dec1);

                const sqrtNum = Number(sqrtPriceX96);
                const q = Math.pow(2, 96);
                const priceUnadj = Math.pow(sqrtNum / q, 2);
                const priceCurrent = priceUnadj * Math.pow(10, dec0 - dec1);

                // Formatted amounts
                const deposited0 = Number(ethers.formatUnits(amount0, dec0));
                const deposited1 = Number(ethers.formatUnits(amount1, dec1));
                const fee0Earned = Number(ethers.formatUnits(fees0, dec0));
                const fee1Earned = Number(ethers.formatUnits(fees1, dec1));
                const pendingCake = Number(ethers.formatUnits(pendingCakeAmount, 18));

                return {
                    tokenId: tokenId.toString(),
                    chain,
                    token0Symbol,
                    token1Symbol,
                    feeTier: Number(fee),
                    deposited0,
                    deposited1,
                    fee0Earned,
                    fee1Earned,
                    pendingCake,
                    priceLower,
                    priceUpper,
                    priceCurrent,
                    isInMasterChef,
                    token0,
                    token1,
                    tickLower: Number(tickLower),
                    tickUpper: Number(tickUpper),
                    currentTick: Number(currentTick),
                    liquidity,
                    valueUSD: 0,
                    token0PriceUSD: 0,
                    token1PriceUSD: 0,
                    cakePriceUSD: 0,
                    pendingCakeUSD: 0,
                    deposited0USD: 0,
                    deposited1USD: 0,
                    fee0EarnedUSD: 0,
                    fee1EarnedUSD: 0,
                };
            } catch (error) {
                console.error(`Error fetching data for tokenId ${tokenId} on ${chain}:`, error);
                return null;
            }
        })
    );

    const filteredPositions = positionsData.filter(Boolean);

    if (filteredPositions.length > 0) {
        const uniqueTokens = new Set();
        filteredPositions.forEach((p) => {
            uniqueTokens.add(p.token0Symbol.toLowerCase());
            uniqueTokens.add(p.token1Symbol.toLowerCase());
        });

        const ids = Array.from(uniqueTokens)
            .map((s) => tokenToId[s])
            .filter(Boolean);
        if (!ids.includes('pancakeswap-token')) {
            ids.push('pancakeswap-token');
        }

        const prices = await fetchPrices(ids);
        const cakePrice = prices['pancakeswap-token']?.usd || 0;

        filteredPositions.forEach((p) => {
            p.token0PriceUSD = prices[tokenToId[p.token0Symbol.toLowerCase()]]?.usd || 0;
            p.token1PriceUSD = prices[tokenToId[p.token1Symbol.toLowerCase()]]?.usd || 0;
            p.cakePriceUSD = cakePrice;
            p.pendingCakeUSD = p.pendingCake * cakePrice;

            p.deposited0USD = p.deposited0 * p.token0PriceUSD;
            p.deposited1USD = p.deposited1 * p.token1PriceUSD;
            p.fee0EarnedUSD = p.fee0Earned * p.token0PriceUSD;
            p.fee1EarnedUSD = p.fee1Earned * p.token1PriceUSD;

            p.valueUSD = p.deposited0USD + p.deposited1USD + p.fee0EarnedUSD + p.fee1EarnedUSD + p.pendingCakeUSD;
        });
    }

    return filteredPositions;
};